// Manages the image table and row operations
export default class TableManager {
  constructor(controller) {
    this.controller = controller
  }

  addImageToTable(data) {
    if (!this.controller.hasImagesTableTarget) return

    const tbody = this.controller.imagesTableTarget.querySelector('tbody')
    if (!tbody) return

    const row = this.createImageRow(data)
    row.dataset.test = `image-row-${this.getRowCount()}`

    const dragDropRow = this.controller.hasDragDropRowTarget ? this.controller.dragDropRowTarget : null
    if (dragDropRow) {
      tbody.insertBefore(row, dragDropRow)
    } else {
      tbody.appendChild(row)
    }

    this.controller.notifySortableController()
  }

  createImageRow(data) {
    const row = document.createElement('tr')
    row.dataset.imagesFieldTarget = 'imageRow'
    row.dataset.imageSortableTarget = 'imageRow'
    row.dataset.attachmentId = data.id
    row.classList.add('image-row')

    row.appendChild(this.createPositionCell())
    row.appendChild(this.createPreviewCell(data))
    row.appendChild(this.createFilenameCell(data))
    row.appendChild(this.createActionCell(data))

    return row
  }

  createPositionCell() {
    const cell = document.createElement('td')
    cell.className = 'image-position-cell'

    const container = document.createElement('div')
    container.className = 'drag-handle-container'

    const dragHandle = document.createElement('img')
    dragHandle.src = this.controller.getAssetPath('drag-handle.svg')
    dragHandle.className = 'drag-handle-icon drag-handle'

    const positionSpan = document.createElement('span')
    positionSpan.className = 'position-number'
    positionSpan.textContent = this.getRowCount() + 1

    container.appendChild(dragHandle)
    container.appendChild(positionSpan)
    cell.appendChild(container)

    return cell
  }

  createPreviewCell(data) {
    const cell = document.createElement('td')
    cell.className = 'image-preview-cell'

    if (data.url) {
      const link = document.createElement('a')
      link.href = data.url
      link.target = '_blank'

      const img = document.createElement('img')
      img.src = data.url
      img.className = 'image-preview'
      link.appendChild(img)

      cell.appendChild(link)
    } else if (data.isTemporary) {
      const img = document.createElement('img')
      img.className = 'image-preview'

      const file = this.controller.fileManager.findFileByName(data.filename)
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      }

      cell.appendChild(img)
    } else {
      cell.appendChild(this.createPlaceholderPreview())
    }

    return cell
  }

  createPlaceholderPreview() {
    const placeholder = document.createElement('div')
    placeholder.className = 'image-placeholder'

    const icon = document.createElement('img')
    icon.src = this.controller.getAssetPath('no-image.svg')
    icon.className = 'empty-state-icon'
    placeholder.appendChild(icon)

    return placeholder
  }

  createFilenameCell(data) {
    const cell = document.createElement('td')
    cell.className = 'image-filename-cell'

    const div = document.createElement('div')
    div.className = 'filename-container'

    if (data.url) {
      const link = document.createElement('a')
      link.href = data.url
      link.target = '_blank'
      link.className = 'filename-link'
      link.textContent = data.filename
      div.appendChild(link)
    } else {
      const span = document.createElement('span')
      span.className = 'position-number'
      span.textContent = data.filename
      div.appendChild(span)
    }

    const fileSize = document.createElement('span')
    fileSize.className = 'file-size'
    fileSize.textContent = this.controller.formatFileSize(data.byte_size)
    div.appendChild(fileSize)

    cell.appendChild(div)
    return cell
  }

  createActionCell(data) {
    const cell = document.createElement('td')
    cell.className = 'image-actions-cell'

    if (data.isTemporary) {
      // For temporary files, create a simple remove button
      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.dataset.action = 'click->images-field#removeTempFile'
      removeButton.dataset.imagesFieldTarget = 'removeButton'
      removeButton.dataset.filename = data.filename
      removeButton.className = 'delete-button'
      removeButton.textContent = this.controller.deleteTextValue

      const srOnly = document.createElement('span')
      srOnly.className = 'sr-only'
      srOnly.textContent = `, ${data.filename}`
      removeButton.appendChild(srOnly)

      cell.appendChild(removeButton)
    } else {
      // For existing attachments, use the standard deletion checkbox
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.name = `${this.controller.recordTypeValue.toLowerCase()}[${this.controller.attributeNameValue}_to_destroy][]`
      checkbox.value = data.id
      checkbox.className = 'hidden'
      checkbox.dataset.imagesFieldTarget = 'deleteCheckbox'
      checkbox.dataset.attachmentId = data.id

      const toggleButton = document.createElement('button')
      toggleButton.type = 'button'
      toggleButton.dataset.action = 'click->images-field#deleteImage'
      toggleButton.dataset.imagesFieldTarget = 'deleteButton'
      toggleButton.dataset.attachmentId = data.id
      toggleButton.className = 'delete-button'
      toggleButton.textContent = this.controller.deleteTextValue

      const srOnly = document.createElement('span')
      srOnly.className = 'sr-only'
      srOnly.textContent = `, ${data.filename}`
      toggleButton.appendChild(srOnly)

      cell.appendChild(checkbox)
      cell.appendChild(toggleButton)
    }

    return cell
  }

  deleteImage(event) {
    event.preventDefault()

    const button = event.currentTarget
    const attachmentId = button.getAttribute("data-attachment-id")
    const row = button.closest("tr")
    const checkbox = this.findDeleteCheckbox(attachmentId)

    if (checkbox && row) {
      checkbox.checked = !checkbox.checked

      if (checkbox.checked) {
        this.markForDeletion(row, button)
      } else {
        this.unmarkForDeletion(row, button)
      }
    }
  }

  findDeleteCheckbox(attachmentId) {
    return this.controller.deleteCheckboxTargets.find(cb =>
      cb.getAttribute("data-attachment-id") === attachmentId
    )
  }

  markForDeletion(row, button) {
    row.classList.add('marked-for-deletion')

    button.classList.remove("delete-button")
    button.classList.add("restore-button")
    button.textContent = this.controller.restoreTextValue
  }

  unmarkForDeletion(row, button) {
    row.classList.remove('marked-for-deletion')

    button.classList.remove("restore-button")
    button.classList.add("delete-button")
    button.textContent = this.controller.deleteTextValue
  }

  getRowCount() {
    return this.controller.imageRowTargets.length
  }
}